import { get, writable } from 'svelte/store';

const NAV_KEY = 'shelly_dev_tab_swipe_navigate';
const ANIM_KEY = 'shelly_dev_tab_swipe_animation';
const IOS_EDGE_KEY = 'shelly_dev_tab_swipe_ios_edge_clearance';

const readBool = (key: string, defaultValue: boolean): boolean => {
	try {
		const stored = localStorage.getItem(key);
		if (stored === null) return defaultValue;
		return stored === 'true';
	} catch {
		return defaultValue;
	}
};

const persist = (key: string, value: boolean) => {
	try {
		localStorage.setItem(key, String(value));
	} catch {
		// localStorage may be unavailable
	}
};

/** When true, horizontal swipe on Dash / Home changes the active tab. Default on. */
export const tabSwipeNavigateEnabled = writable<boolean>(readBool(NAV_KEY, true));

tabSwipeNavigateEnabled.subscribe((value) => persist(NAV_KEY, value));

export const isTabSwipeNavigateEnabled = (): boolean => get(tabSwipeNavigateEnabled);

export const setTabSwipeNavigateEnabled = (enabled: boolean) => {
	tabSwipeNavigateEnabled.set(enabled);
};

/** Directional fade/slide after a Dash / Home *swipe* tab change. Default on. */
export const tabSwipeAnimationEnabled = writable<boolean>(readBool(ANIM_KEY, true));

tabSwipeAnimationEnabled.subscribe((value) => persist(ANIM_KEY, value));

export const isTabSwipeAnimationEnabled = (): boolean => get(tabSwipeAnimationEnabled);

export const setTabSwipeAnimationEnabled = (enabled: boolean) => {
	tabSwipeAnimationEnabled.set(enabled);
};

/**
 * When true on iOS: disable WKWebView back/forward edge gestures on Dash / Home,
 * and ignore tab-swipes that start in the outer edge band. Default off.
 */
export const tabSwipeIosEdgeClearanceEnabled = writable<boolean>(readBool(IOS_EDGE_KEY, false));

tabSwipeIosEdgeClearanceEnabled.subscribe((value) => persist(IOS_EDGE_KEY, value));

export const isTabSwipeIosEdgeClearanceEnabled = (): boolean => get(tabSwipeIosEdgeClearanceEnabled);

export const setTabSwipeIosEdgeClearanceEnabled = (enabled: boolean) => {
	tabSwipeIosEdgeClearanceEnabled.set(enabled);
};

/** Last swipe direction: `1` = forward (enter from right), `-1` = back. */
export const tabSwipeAnimDirection = writable<1 | -1>(1);

export const setTabSwipeAnimDirection = (direction: 1 | -1) => {
	tabSwipeAnimDirection.set(direction);
};

/** True only for the next location change that was started by a tab swipe. */
let tabSwipeAnimPending = false;

/** Mark the coming Dash / Home navigation as swipe-initiated so it can animate. */
export const requestTabSwipeContentAnimation = (direction: 1 | -1) => {
	tabSwipeAnimDirection.set(direction);
	tabSwipeAnimPending = true;
};

/** Consume the swipe-pending flag. Clicks never set it, so they do not animate. */
export const consumeTabSwipeAnimPending = (): boolean => {
	const pending = tabSwipeAnimPending;
	tabSwipeAnimPending = false;
	return pending;
};
